import { Link } from "react-router-dom";
import { Calendar, MapPin, Music } from "lucide-react";
import { fileUrl, thumbUrl } from "../lib/api";
import { useListingImage } from "../lib/termImages";
import { availabilityLabel } from "./Marketplace";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
  catch { return d; }
};

const fmtTime = (t) => {
  if (!t) return "";
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
};

const fmtDateTime = (d, t) => [fmtDate(d), fmtTime(t)].filter(Boolean).join(" · ");

const dateParts = (d) => {
  try { const dt = new Date(d + "T00:00:00"); if (isNaN(dt)) return { mo: "", day: "" }; return { mo: dt.toLocaleDateString("en-US", { month: "short" }), day: dt.getDate() }; }
  catch { return { mo: "", day: "" }; }
};

const FALLBACK_IMG = "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg?w=800";
const CARD = "group block rounded-xl overflow-hidden bg-[#111] border border-white/5 card-hover";

// Grid classes per card style — used by directory pages & homepage sections.
export const cardGrid = (style, cols = "sm:grid-cols-2 lg:grid-cols-3") =>
  style === "horizontal" ? "grid-cols-1 lg:grid-cols-2"
    : style === "compact" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
      : style === "poster" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
        : style === "minimal" ? "grid-cols-1"
          : `grid-cols-1 ${cols}`;

const PREMIUM = "group block rounded-xl overflow-hidden bg-gradient-to-b from-[#18181d] to-[#0c0c0f] border border-amber-400/25 card-hover";
const LIGHT = "group block rounded-xl overflow-hidden bg-white border border-zinc-200 card-hover";
const MINIMAL = "group flex items-center gap-4 rounded-lg bg-[#111] border border-white/5 px-4 py-3 card-hover";

const VipChip = () => <span className="bg-amber-400 text-black text-[10px] font-extrabold px-2 py-0.5 rounded-full">VIP</span>;
const SpotChip = ({ text }) => <span className="bg-rose-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">★ {text}</span>;
const AwardChip = ({ badges }) => {
  if (!badges?.length) return null;
  const b = badges[0];
  const win = ["winner", "artist_of_year", "venue_of_year", "category_winner"].includes(b.badge_type);
  const label = { nominated: "Nominated", finalist: "Finalist", winner: "Winner", category_winner: "Winner", artist_of_year: "Artist of the Year", venue_of_year: "Venue of the Year" }[b.badge_type] || b.badge_type;
  return <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${win ? "bg-amber-400 text-black" : "bg-sky-500/30 text-sky-200"}`}>🏆 {label}</span>;
};

// ----------------- EVENT -----------------
export function EventCard({ e, style = "default" }) {
  const fb = useListingImage(e, "event");
  const img = thumbUrl(e.profile_image || e.event_image) || fb.src || FALLBACK_IMG;
  const dateLabel = e.end_date && e.end_date !== e.event_date ? `${fmtDate(e.event_date)} – ${fmtDate(e.end_date)}` : fmtDateTime(e.event_date, e.start_time);
  const tid = `event-card-${e.slug}`;

  if (style === "horizontal") {
    return (
      <Link to={`/events/${e.slug}`} className={`${CARD} flex`} data-testid={tid}>
        <div className="relative w-32 sm:w-44 shrink-0 overflow-hidden"><img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-4 min-w-0 flex-1">
          <p className="overline mb-1">{e.genre}</p>
          <h3 className="font-display font-bold text-lg leading-tight line-clamp-2">{e.title}</h3>
          <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1"><Calendar size={12} /> {dateLabel}</p>
          <p className="text-sm text-zinc-300 mt-1 flex items-center gap-1 truncate"><MapPin size={13} /> {e.venue_name}, {e.city}</p>
        </div>
      </Link>
    );
  }
  if (style === "large") {
    return (
      <Link to={`/events/${e.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[3/2] overflow-hidden">
          <img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 left-3 glass rounded-full px-3 py-1 text-xs font-bold">{dateLabel}</div>
          {e.featured && <div className="absolute top-3 right-3 bg-rose-500 rounded-full px-3 py-1 text-xs font-bold">{e.boost_badge || "Featured"}</div>}
        </div>
        <div className="p-5">
          <p className="overline mb-1">{e.genre}</p>
          <h3 className="font-display font-bold text-2xl leading-tight">{e.title}</h3>
          <p className="text-sm text-zinc-300 mt-2 flex items-center gap-1"><MapPin size={13} /> {e.venue_name}, {e.city}</p>
        </div>
      </Link>
    );
  }
  if (style === "compact") {
    return (
      <Link to={`/events/${e.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[16/10] overflow-hidden">
          <img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute bottom-2 inset-x-3">
            <h3 className="font-display font-bold text-sm leading-tight truncate">{e.title}</h3>
            <p className="text-[11px] text-zinc-300 truncate">{dateLabel} · {e.city}</p>
          </div>
        </div>
      </Link>
    );
  }
  if (style === "premium") {
    return (
      <Link to={`/events/${e.slug}`} className={PREMIUM} data-testid={tid}>
        <div className="relative aspect-video overflow-hidden">
          <img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 left-3 bg-amber-400 text-black rounded-full px-3 py-1 text-xs font-extrabold">{dateLabel}</div>
        </div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400 font-bold mb-1">{e.genre}</p>
          <h3 className="font-display font-bold text-xl leading-tight">{e.title}</h3>
          <p className="text-sm text-zinc-400 mt-2 flex items-center gap-1"><MapPin size={13} /> {e.venue_name}, {e.city}</p>
        </div>
      </Link>
    );
  }
  if (style === "light") {
    return (
      <Link to={`/events/${e.slug}`} className={LIGHT} data-testid={tid}>
        <div className="relative aspect-video overflow-hidden"><img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-widest text-rose-600 font-bold mb-1">{e.genre}</p>
          <h3 className="font-display font-bold text-lg leading-tight text-zinc-900">{e.title}</h3>
          <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1"><Calendar size={12} /> {dateLabel}</p>
          <p className="text-sm text-zinc-600 mt-1 flex items-center gap-1"><MapPin size={13} /> {e.venue_name}, {e.city}</p>
        </div>
      </Link>
    );
  }
  if (style === "poster") {
    return (
      <Link to={`/events/${e.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[2/3] overflow-hidden">
          <img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/40" />
          <div className="absolute top-3 inset-x-3 text-center"><span className="glass rounded-full px-3 py-1 text-[11px] font-bold">{dateLabel}</span></div>
          <div className="absolute bottom-0 p-4 w-full text-center">
            <p className="overline mb-1">{e.genre}</p>
            <h3 className="font-display font-extrabold text-xl uppercase leading-tight">{e.title}</h3>
            <p className="text-xs text-zinc-300 mt-1.5">{e.venue_name} · {e.city}</p>
          </div>
        </div>
      </Link>
    );
  }
  if (style === "minimal") {
    const { mo, day } = dateParts(e.event_date);
    return (
      <Link to={`/events/${e.slug}`} className={MINIMAL} data-testid={tid}>
        <div className="text-center shrink-0 w-12">
          <p className="text-[10px] uppercase text-rose-400 font-bold">{mo}</p>
          <p className="font-display font-bold text-xl leading-none">{day}</p>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-base truncate group-hover:text-rose-400 transition-colors">{e.title}</h3>
          <p className="text-xs text-zinc-400 truncate">{e.venue_name}, {e.city}{e.start_time ? ` · ${fmtTime(e.start_time)}` : ""}</p>
        </div>
        <span className="text-zinc-600 group-hover:text-rose-400 transition-colors shrink-0">→</span>
      </Link>
    );
  }
  return (
    <Link to={`/events/${e.slug}`} className={CARD} data-testid={tid}>
      <div className="relative aspect-[4/5] overflow-hidden">
        <img loading="lazy" src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute top-3 left-3 glass rounded-full px-3 py-1 text-xs font-bold">{dateLabel}</div>
        {e.featured && <div className="absolute top-3 right-3 bg-rose-500 rounded-full px-3 py-1 text-xs font-bold">{e.boost_badge || "Featured"}</div>}
        <div className="absolute bottom-0 p-4 w-full">
          <p className="overline mb-1">{e.genre}</p>
          <h3 className="font-display font-bold text-lg leading-tight">{e.title}</h3>
          <p className="text-sm text-zinc-300 mt-1 flex items-center gap-1"><MapPin size={13} /> {e.venue_name}, {e.city}</p>
        </div>
      </div>
    </Link>
  );
}

// ----------------- VENUE -----------------
export function VenueCard({ v, style = "default" }) {
  const fb = useListingImage(v, "venue");
  const img = thumbUrl(v.hero_image) || fb.src || FALLBACK_IMG;
  const tid = `venue-card-${v.slug}`;

  if (style === "horizontal") {
    return (
      <Link to={`/venues/${v.slug}`} className={`${CARD} flex`} data-testid={tid}>
        <div className="relative w-32 sm:w-44 shrink-0 overflow-hidden"><img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-4 min-w-0 flex-1">
          <p className="overline mb-1">{v.venue_type}</p>
          <h3 className="font-display font-bold text-lg line-clamp-2">{v.name}</h3>
          <p className="text-sm text-zinc-300 mt-1.5 flex items-center gap-1 truncate"><MapPin size={13} /> {v.city}, {v.state}</p>
        </div>
      </Link>
    );
  }
  if (style === "large") {
    return (
      <Link to={`/venues/${v.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[3/2] overflow-hidden"><img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-5">
          <p className="overline mb-1">{v.venue_type}</p>
          <h3 className="font-display font-bold text-2xl">{v.name}</h3>
          <p className="text-sm text-zinc-300 mt-2 flex items-center gap-1"><MapPin size={13} /> {v.city}, {v.state}</p>
        </div>
      </Link>
    );
  }
  if (style === "compact") {
    return (
      <Link to={`/venues/${v.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[16/10] overflow-hidden">
          <img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
          <div className="absolute bottom-2 inset-x-3"><h3 className="font-display font-bold text-sm truncate">{v.name}</h3><p className="text-[11px] text-zinc-300 truncate">{v.city}, {v.state}</p></div>
        </div>
      </Link>
    );
  }
  if (style === "premium") {
    return (
      <Link to={`/venues/${v.slug}`} className={PREMIUM} data-testid={tid}>
        <div className="relative aspect-video overflow-hidden">
          <img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          {v.vip && <div className="absolute top-3 right-3"><VipChip /></div>}
        </div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400 font-bold mb-1">{v.venue_type}</p>
          <h3 className="font-display font-bold text-xl">{v.name}</h3>
          <p className="text-sm text-zinc-400 mt-2 flex items-center gap-1"><MapPin size={13} /> {v.city}, {v.state}</p>
        </div>
      </Link>
    );
  }
  if (style === "light") {
    return (
      <Link to={`/venues/${v.slug}`} className={LIGHT} data-testid={tid}>
        <div className="relative aspect-video overflow-hidden"><img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-widest text-rose-600 font-bold mb-1">{v.venue_type}</p>
          <h3 className="font-display font-bold text-lg text-zinc-900">{v.name}</h3>
          <p className="text-sm text-zinc-600 mt-1 flex items-center gap-1"><MapPin size={13} /> {v.city}, {v.state}</p>
        </div>
      </Link>
    );
  }
  if (style === "poster") {
    return (
      <Link to={`/venues/${v.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[2/3] overflow-hidden">
          <img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/40" />
          <div className="absolute bottom-0 p-4 w-full text-center">
            <p className="overline mb-1">{v.venue_type}</p>
            <h3 className="font-display font-extrabold text-xl uppercase leading-tight">{v.name}</h3>
            <p className="text-xs text-zinc-300 mt-1.5">{v.city}, {v.state}</p>
          </div>
        </div>
      </Link>
    );
  }
  if (style === "minimal") {
    return (
      <Link to={`/venues/${v.slug}`} className={MINIMAL} data-testid={tid}>
        <div className="shrink-0 w-10 h-10 rounded-lg bg-rose-500/15 flex items-center justify-center"><MapPin size={16} className="text-rose-400" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-base truncate group-hover:text-rose-400 transition-colors">{v.name}</h3>
          <p className="text-xs text-zinc-400 truncate">{v.venue_type} · {v.city}, {v.state}</p>
        </div>
        {v.vip && <VipChip />}
        <span className="text-zinc-600 group-hover:text-rose-400 transition-colors shrink-0">→</span>
      </Link>
    );
  }
  return (
    <Link to={`/venues/${v.slug}`} className={CARD} data-testid={tid}>
      <div className="relative aspect-video overflow-hidden">
        <img loading="lazy" src={img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
        {(v.vip || v.spotlight_badge || v.award_badges?.length > 0) && (
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
            {v.spotlight_badge && <SpotChip text={v.spotlight_badge} />}
            {v.vip && <VipChip />}
            <AwardChip badges={v.award_badges} />
          </div>
        )}
        <div className="absolute bottom-0 p-4">
          <p className="overline mb-1">{v.venue_type}</p>
          <h3 className="font-display font-bold text-lg">{v.name}</h3>
          <p className="text-sm text-zinc-300 flex items-center gap-1"><MapPin size={13} /> {v.city}, {v.state}</p>
        </div>
      </div>
    </Link>
  );
}

// ----------------- ARTIST -----------------
function ArtistBadge({ a }) {
  const avail = availabilityLabel(a.availability);
  const tone = { emerald: "bg-emerald-400", amber: "bg-amber-400", zinc: "bg-zinc-400" };
  if (avail) return <div className="flex items-center gap-1.5 glass rounded-full px-2.5 py-1 text-xs"><span className={`w-2 h-2 rounded-full ${tone[avail.tone]}`} /> {avail.text}</div>;
  if (a.booking_available) return <div className="flex items-center gap-1.5 glass rounded-full px-2.5 py-1 text-xs"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Booking</div>;
  return null;
}

export function ArtistCard({ a, style = "default" }) {
  const fb = useListingImage(a, "artist");
  const img = thumbUrl(a.profile_image || a.hero_image) || fb.src || FALLBACK_IMG;
  const tid = `artist-card-${a.slug}`;
  const genreLabel = (a.genres && a.genres.length ? a.genres : [a.genre]).filter(Boolean).join(" \u00b7 ");

  if (style === "horizontal") {
    return (
      <Link to={`/artists/${a.slug}`} className={`${CARD} flex`} data-testid={tid}>
        <div className="relative w-28 sm:w-36 shrink-0 overflow-hidden"><img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-4 min-w-0 flex-1">
          <p className="overline mb-1 flex items-center gap-1"><Music size={11} /> {genreLabel}</p>
          <h3 className="font-display font-bold text-lg line-clamp-2">{a.name}</h3>
          <p className="text-sm text-zinc-300 mt-1 truncate">{a.home_city}, {a.home_state}</p>
          <div className="mt-2"><ArtistBadge a={a} /></div>
        </div>
      </Link>
    );
  }
  if (style === "large") {
    return (
      <Link to={`/artists/${a.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-square overflow-hidden">
          <img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          {a.logo && <img src={fileUrl(a.logo)} alt="" className="absolute top-3 left-3 w-10 h-10 rounded-lg object-cover border border-white/20" />}
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">{a.vip && <VipChip />}<ArtistBadge a={a} /></div>
        </div>
        <div className="p-5"><p className="overline mb-1 flex items-center gap-1"><Music size={11} /> {genreLabel}</p><h3 className="font-display font-bold text-2xl">{a.name}</h3><p className="text-sm text-zinc-300 mt-1">{a.home_city}, {a.home_state}</p></div>
      </Link>
    );
  }
  if (style === "compact") {
    return (
      <Link to={`/artists/${a.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-square overflow-hidden">
          <img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
          <div className="absolute bottom-2 inset-x-3"><h3 className="font-display font-bold text-sm truncate">{a.name}</h3><p className="text-[11px] text-zinc-300 truncate">{genreLabel} · {a.home_city}</p></div>
        </div>
      </Link>
    );
  }
  if (style === "premium") {
    return (
      <Link to={`/artists/${a.slug}`} className={PREMIUM} data-testid={tid}>
        <div className="relative aspect-square overflow-hidden">
          <img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">{a.vip && <VipChip />}<ArtistBadge a={a} /></div>
        </div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400 font-bold mb-1 flex items-center gap-1"><Music size={11} /> {genreLabel}</p>
          <h3 className="font-display font-bold text-xl">{a.name}</h3>
          <p className="text-sm text-zinc-400 mt-1">{a.home_city}, {a.home_state}</p>
        </div>
      </Link>
    );
  }
  if (style === "light") {
    return (
      <Link to={`/artists/${a.slug}`} className={LIGHT} data-testid={tid}>
        <div className="relative aspect-square overflow-hidden"><img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
        <div className="p-5">
          <p className="text-[11px] uppercase tracking-widest text-rose-600 font-bold mb-1 flex items-center gap-1"><Music size={11} /> {genreLabel}</p>
          <h3 className="font-display font-bold text-lg text-zinc-900">{a.name}</h3>
          <p className="text-sm text-zinc-600 mt-1">{a.home_city}, {a.home_state}</p>
        </div>
      </Link>
    );
  }
  if (style === "poster") {
    return (
      <Link to={`/artists/${a.slug}`} className={CARD} data-testid={tid}>
        <div className="relative aspect-[2/3] overflow-hidden">
          <img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/40" />
          <div className="absolute bottom-0 p-4 w-full text-center">
            <p className="overline mb-1">{genreLabel}</p>
            <h3 className="font-display font-extrabold text-xl uppercase leading-tight">{a.name}</h3>
            <p className="text-xs text-zinc-300 mt-1.5">{a.home_city}, {a.home_state}</p>
          </div>
        </div>
      </Link>
    );
  }
  if (style === "minimal") {
    return (
      <Link to={`/artists/${a.slug}`} className={MINIMAL} data-testid={tid}>
        <img loading="lazy" src={img} alt={a.name} className="shrink-0 w-10 h-10 rounded-full object-cover border border-white/10" />
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-base truncate group-hover:text-rose-400 transition-colors">{a.name}</h3>
          <p className="text-xs text-zinc-400 truncate">{genreLabel} · {a.home_city}, {a.home_state}</p>
        </div>
        {a.vip && <VipChip />}
        <span className="text-zinc-600 group-hover:text-rose-400 transition-colors shrink-0">→</span>
      </Link>
    );
  }
  return (
    <Link to={`/artists/${a.slug}`} className={CARD} data-testid={tid}>
      <div className="relative aspect-[3/4] overflow-hidden">
        <img loading="lazy" src={img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        {a.logo && <img src={fileUrl(a.logo)} alt="" className="absolute top-3 left-3 w-10 h-10 rounded-lg object-cover border border-white/20" />}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">{a.spotlight_badge && <SpotChip text={a.spotlight_badge} />}{a.vip && <VipChip />}<AwardChip badges={a.award_badges} /><ArtistBadge a={a} /></div>
        <div className="absolute bottom-0 p-4">
          <p className="overline mb-1 flex items-center gap-1"><Music size={11} /> {genreLabel}</p>
          <h3 className="font-display font-bold text-lg">{a.name}</h3>
          <p className="text-sm text-zinc-300">{a.home_city}, {a.home_state}</p>
        </div>
      </div>
    </Link>
  );
}

export { fmtDate, fmtTime, fmtDateTime };

export function GigCard({ g }) {
  const deadline = g.application_deadline ? new Date(g.application_deadline + "T23:59:59") : null;
  const daysLeft = deadline ? Math.ceil((deadline - new Date()) / 86400000) : null;
  return (
    <Link to={`/gigs/${g.slug}`} className="group block rounded-2xl overflow-hidden bg-[#111] border border-white/10 card-hover p-5" data-testid={`gig-card-${g.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline">{g.performance_type_needed}</span>
        {daysLeft != null && daysLeft >= 0 && <span className="text-xs glass rounded-full px-2.5 py-1">{daysLeft === 0 ? "Closes today" : `${daysLeft}d to apply`}</span>}
      </div>
      <h3 className="font-display font-bold text-lg leading-tight group-hover:text-rose-400 transition-colors">{g.title}</h3>
      <p className="text-sm text-zinc-400 flex items-center gap-1 mt-2"><MapPin size={13} /> {g.location_name || g.venue_name}, {g.city}, {g.state}</p>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
        <span className="text-sm text-zinc-300"><Calendar size={13} className="inline mr-1" />{fmtDate(g.event_date)}</span>
        {(g.budget_min || g.budget_max) && <span className="font-display font-bold text-rose-400">${g.budget_min}{g.budget_max ? `–$${g.budget_max}` : ""}</span>}
      </div>
    </Link>
  );
}

export function AttractionCard({ a }) {
  const fb = useListingImage(a, "attraction");
  const img = thumbUrl(a.hero_image || a.logo) || fb.src || FALLBACK_IMG;
  return (
    <Link to={`/attractions/${a.slug}`} className="group block rounded-xl overflow-hidden bg-[#111] border border-white/10 card-hover" data-testid={`attraction-card-${a.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img loading="lazy" src={img} alt={fb.alt || a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {a.featured && <span className="absolute top-3 left-3 bg-rose-500 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Featured</span>}
        {a.vip && <span className="absolute top-3 right-3 bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">VIP</span>}
      </div>
      <div className="p-4">
        {a.attraction_type && <p className="overline !text-[10px] mb-1">{a.attraction_type}</p>}
        <h3 className="font-display font-bold text-lg leading-tight group-hover:text-rose-400 transition-colors truncate">{a.name}</h3>
        <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1.5"><MapPin size={13} /> {[a.city, a.state].filter(Boolean).join(", ") || "Gulf Coast"}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-sm">
          <span className="text-zinc-400">{a.family_friendly ? "Family friendly" : a.indoor_outdoor || ""}</span>
          {a.admission_price && <span className="font-display font-bold text-rose-400">{String(a.admission_price).startsWith("$") ? a.admission_price : `$${a.admission_price}`}</span>}
        </div>
      </div>
    </Link>
  );
}

export function BusinessCard({ b }) {
  const fb = useListingImage(b, "business");
  const img = thumbUrl(b.hero_image || b.logo) || fb.src || FALLBACK_IMG;
  return (
    <Link to={`/businesses/${b.slug}`} className="group block rounded-xl overflow-hidden bg-[#111] border border-white/10 card-hover" data-testid={`business-card-${b.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img loading="lazy" src={img} alt={fb.alt || b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {b.featured && <span className="absolute top-3 left-3 bg-rose-500 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Featured</span>}
        {b.logo && <img src={thumbUrl(b.logo)} alt="" className="absolute bottom-3 left-3 w-10 h-10 rounded-lg object-cover border border-white/20" />}
      </div>
      <div className="p-4">
        {b.business_type && <p className="overline !text-[10px] mb-1">{b.business_type}</p>}
        <h3 className="font-display font-bold text-lg leading-tight group-hover:text-rose-400 transition-colors truncate">{b.name}</h3>
        <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1.5"><MapPin size={13} /> {[b.city, b.state].filter(Boolean).join(", ") || "Gulf Coast"}</p>
      </div>
    </Link>
  );
}

export function ActivityCard({ a }) {
  const fb = useListingImage(a, "activity");
  const img = thumbUrl(a.hero_image || a.logo) || fb.src || FALLBACK_IMG;
  return (
    <Link to={`/activities/${a.slug}`} className="group block rounded-xl overflow-hidden bg-[#111] border border-white/10 card-hover" data-testid={`activity-card-${a.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img loading="lazy" src={img} alt={fb.alt || a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {a.featured && <span className="absolute top-3 left-3 bg-rose-500 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Featured</span>}
        {a.vip && <span className="absolute top-3 right-3 bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">VIP</span>}
        {a.duration && <span className="absolute bottom-3 right-3 glass text-[11px] px-2.5 py-1 rounded-full">{a.duration}</span>}
      </div>
      <div className="p-4">
        {a.activity_type && <p className="overline !text-[10px] mb-1">{a.activity_type}</p>}
        <h3 className="font-display font-bold text-lg leading-tight group-hover:text-rose-400 transition-colors truncate">{a.name}</h3>
        <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1.5"><MapPin size={13} /> {[a.city, a.state].filter(Boolean).join(", ") || "Gulf Coast"}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-sm">
          <span className="text-zinc-400">{a.booking_mode === "external" ? "Book online" : "Request booking"}</span>
          {a.price && <span className="font-display font-bold text-rose-400">{String(a.price).startsWith("$") ? a.price : `$${a.price}`}<span className="text-xs text-zinc-500 font-normal">/person</span></span>}
        </div>
      </div>
    </Link>
  );
}
